import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Select, MenuItem, FormControl, InputLabel,
    Box, Divider, Typography, FormHelperText, CircularProgress
} from "@mui/material";
import WODSearch from "./WODSearch";
import {
    getTodayWOD,
    saveTodayWOD,
    getTodayWODWorkoutImage,
    uploadTodayWODWorkoutImage,
    deleteTodayWODWorkoutImage
} from "../api/wodApi";
import DOMPurify from 'dompurify';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import 'react-quill/dist/quill.snow.css';
import { formatDateKeyLong, toCivilDateKey } from "../utils/affiliateTimezone";

export default function WODModal({
    open,
    onClose,
    selectedDate,
    timeZone = "Europe/Tallinn",
    selectedAffiliateId,
    refreshWODs,
    onSaveSuccess,
    onSaveError,
    saveCompetitionToCompeteDay = false,
    competitionInfoOverride,
    onSaveCompetitionInfo,
    selectedClassType = "WOD"
}) {
    const dialogZIndex = 2100;

    const [wod, setWod] = useState({
        wodName: "",
        type: "For Time",
        description: "",
        descriptionIntermediate: "",
        descriptionBeginner: "",
        descriptionSkillWork: "",
        descriptionStretching: "",
        descriptionAtHome: "",
        notes: "",
        competitionInfo: "",
        videoLinks: []
    });
    const [showSearch, setShowSearch] = useState(true);

    const [errors, setErrors] = useState({ description: false });
    const [videoLinkErrors, setVideoLinkErrors] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [imageError, setImageError] = useState("");
    const [existingWorkoutImage, setExistingWorkoutImage] = useState(null);
    const [selectedWorkoutImageFile, setSelectedWorkoutImageFile] = useState(null);
    const [selectedWorkoutImagePreview, setSelectedWorkoutImagePreview] = useState("");
    const [removeWorkoutImage, setRemoveWorkoutImage] = useState(false);
    const workoutImageInputRef = useRef(null);
    const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
    const MAX_UPLOAD_IMAGE_BYTES = 15 * 1024 * 1024;
    const getEditorSx = (hasError = false) => ({
        '& .ql-toolbar': {
            borderColor: hasError ? '#d32f2f' : '#AAAAAA',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
        },
        '& .ql-container': {
            minHeight: '120px',
            borderColor: hasError ? '#d32f2f' : '#AAAAAA',
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
            fontSize: '14px'
        },
        '& .ql-editor': {
            minHeight: '120px',
            lineHeight: '1.35',
            paddingTop: '8px',
            paddingBottom: '8px'
        }
    });


    const wodTypes = ["For Time", "EMOM", "TABATA", "AMRAP", "For Load", "For Quality", "NONE"];

    const quillModules = useMemo(() => ({
        toolbar: [
            ["bold", "italic"],
            [{ color: ["#c62929", "#1a7e1a", "#3434d6", "#a1a166", "#ffa500", "#800080", false] }],
            ["clean"]
        ]
    }), []);

    const quillFormats = useMemo(() => ["bold", "italic", "color"], []);
    const parseVideoLinks = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value.filter((link) => typeof link === "string").map((link) => link.trim()).filter(Boolean);
        }
        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                    return parsed.filter((link) => typeof link === "string").map((link) => link.trim()).filter(Boolean);
                }
            } catch (error) {
                return [];
            }
        }
        return [];
    };

    const isValidYouTubeLink = (url) => {
        if (!url || typeof url !== "string") return false;
        try {
            const parsed = new URL(url.trim());
            const host = parsed.hostname.replace("www.", "");
            if (host === "youtu.be") {
                return parsed.pathname.length > 1;
            }
            if (host === "youtube.com" || host === "m.youtube.com") {
                if (parsed.pathname === "/watch") {
                    return !!parsed.searchParams.get("v");
                }
                return parsed.pathname.startsWith("/shorts/") || parsed.pathname.startsWith("/embed/");
            }
            return false;
        } catch (error) {
            return false;
        }
    };

    const compressImageInBrowser = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const maxDim = 1600;
                const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
                const width = Math.round(img.width * ratio);
                const height = Math.round(img.height * ratio);
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                if (!ctx) return reject(new Error("Failed to prepare image compression"));
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) return reject(new Error("Failed to compress image"));
                        const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
                        resolve(compressedFile);
                    },
                    "image/jpeg",
                    0.82
                );
            };
            img.onerror = () => reject(new Error("Invalid image file"));
            img.src = String(reader.result);
        };
        reader.onerror = () => reject(new Error("Failed to read image file"));
        reader.readAsDataURL(file);
    });

    const toEditorHtml = (value) => {
        if (!value) return "";

        const hasHtml = /<\/?[a-z][\s\S]*>/i.test(value);
        if (!hasHtml) {
            return value
                .replace(/\r\n/g, "\n")
                .replace(/\r/g, "\n")
                .replace(/\n/g, "<br>");
        }

        let html = DOMPurify.sanitize(value, {
            ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "span"],
            ALLOWED_ATTR: ["style"],
            ALLOWED_STYLES: {
                "*": {
                    color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                }
            },
            KEEP_CONTENT: true
        });

        // Normalize legacy tags to what Quill handles best.
        html = html
            .replace(/<b(\s|>)/gi, "<strong$1")
            .replace(/<\/b>/gi, "</strong>")
            .replace(/<i(\s|>)/gi, "<em$1")
            .replace(/<\/i>/gi, "</em>")
            .replace(/^\s*<br\s*\/?>/gi, "")
            .replace(/<br\s*\/?>\s*$/gi, "");

        const hasBlock = /<(p|div|h[1-6]|ul|ol|li|blockquote|pre)\b/i.test(html);
        if (!hasBlock && html.trim()) {
            html = `<p>${html}</p>`;
        }

        return html;
    };

    const stripHtml = (html) => {
        if (!html) return "";
        const sanitized = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
        return sanitized.replace(/\u00a0/g, " ").trim();
    };

    const cleanQuillHTML = (html) => {
        if (!html) return "";

        return html
            .replace(/>\s*\n\s*</g, "><")
            // Allow intentional extra spacing, but cap runaway empty blocks.
            .replace(/(<p><br><\/p>){4,}/gi, "<p><br></p><p><br></p><p><br></p>")
            .replace(/^(<p><br><\/p>)+/gi, "")
            .replace(/(<p><br><\/p>)+$/gi, "")
            .trim();
    };

    // Load WOD data when modal opens
    useEffect(() => {
        if (open && selectedDate) {
            const loadData = async () => {
                try {
                    const response = await getTodayWOD(selectedAffiliateId, selectedDate, selectedClassType);
                    if (response) {
                        const parsedVideoLinks = parseVideoLinks(response.videoLinks);
                        setWod({
                            wodName: response.wodName || "",
                            type: response.type || "For Time",
                            description: toEditorHtml(response.description || ""),
                            descriptionIntermediate: toEditorHtml(response.descriptionIntermediate || ""),
                            descriptionBeginner: toEditorHtml(response.descriptionBeginner || ""),
                            descriptionSkillWork: toEditorHtml(response.descriptionSkillWork || ""),
                            descriptionStretching: toEditorHtml(response.descriptionStretching || ""),
                            descriptionAtHome: toEditorHtml(response.descriptionAtHome || ""),
                            notes: response.notes || "",
                            competitionInfo: toEditorHtml(
                                typeof competitionInfoOverride === "string"
                                    ? competitionInfoOverride
                                    : (response.competitionInfo || "")
                            ),
                            videoLinks: parsedVideoLinks
                        });
                        setErrors({ description: false });
                        setVideoLinkErrors(new Array(parsedVideoLinks.length).fill(""));
                        if (response.id) {
                            const imageData = await getTodayWODWorkoutImage(response.id);
                            setExistingWorkoutImage(imageData?.imageData || null);
                        } else {
                            setExistingWorkoutImage(null);
                        }
                    } else {
                        setWod({
                            wodName: "",
                            type: "For Time",
                            description: "",
                            descriptionIntermediate: "",
                            descriptionBeginner: "",
                            descriptionSkillWork: "",
                            descriptionStretching: "",
                            descriptionAtHome: "",
                            notes: "",
                            competitionInfo: typeof competitionInfoOverride === "string" ? toEditorHtml(competitionInfoOverride) : "",
                            videoLinks: []
                        });
                        setVideoLinkErrors([]);
                        setExistingWorkoutImage(null);
                    }
                } catch (error) {
                    console.error("Error loading WOD:", error);
                }
            };

            loadData();
            setShowSearch(true);
            setImageError("");
            setSelectedWorkoutImageFile(null);
            setSelectedWorkoutImagePreview("");
            setRemoveWorkoutImage(false);
        }
    }, [open, selectedDate, selectedAffiliateId, competitionInfoOverride, selectedClassType]);

    useEffect(() => () => {
        if (selectedWorkoutImagePreview) {
            URL.revokeObjectURL(selectedWorkoutImagePreview);
        }
    }, [selectedWorkoutImagePreview]);

    // Force reliable plain-text paste in Quill editors (Ctrl+V + context-menu Paste).
    const handleQuillPasteCapture = async (event) => {
        const editorEl = event.target?.closest?.(".ql-editor");
        if (!editorEl) return;

        let text = event.clipboardData?.getData("text/plain") || "";
        if (!text && navigator.clipboard?.readText) {
            try {
                text = await navigator.clipboard.readText();
            } catch (err) {
                // Ignore clipboard permission/read errors and let default behavior continue.
            }
        }
        if (!text) return;

        event.preventDefault();
        const containerEl = editorEl.closest(".ql-container");
        const quill =
            containerEl?.__quill ||
            Quill.find(containerEl) ||
            Quill.find(editorEl);
        if (!quill || typeof quill.getSelection !== "function" || typeof quill.insertText !== "function") return;

        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertText(index, text, "user");
        quill.setSelection(index + text.length, 0, "silent");
    };

    const validateForm = () => {
        const hasAnyTrack = [
            wod.description,
            wod.descriptionIntermediate,
            wod.descriptionBeginner,
            wod.descriptionSkillWork,
            wod.descriptionStretching,
            wod.descriptionAtHome
        ].some((value) => stripHtml(value));

        const newErrors = {
            description: !hasAnyTrack
        };
        setErrors(newErrors);
        const links = Array.isArray(wod.videoLinks) ? wod.videoLinks : [];
        const normalizedLinks = links.map((link) => (typeof link === "string" ? link.trim() : ""));
        const linkErrors = normalizedLinks.map((link) => {
            if (!link) return "";
            return isValidYouTubeLink(link) ? "" : "Please enter a valid YouTube link";
        });
        setVideoLinkErrors(linkErrors);
        const hasVideoErrors = linkErrors.some(Boolean);
        return !Object.values(newErrors).some(error => error) && !hasVideoErrors && !imageError;
    };

    const handleSaveWOD = async () => {
        const isValid = validateForm();
        if (!isValid) {
            return;
        }

        setIsSaving(true);
        try {
            const formattedDate = toCivilDateKey(selectedDate, timeZone);

            const sanitizedDescription = DOMPurify.sanitize(cleanQuillHTML(wod.description), {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'span'],
                ALLOWED_ATTR: ['style'],
                ALLOWED_STYLES: {
                    '*': {
                        color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                    }
                },
                KEEP_CONTENT: true
            });

            const sanitizedCompetitionInfo = DOMPurify.sanitize(cleanQuillHTML(wod.competitionInfo), {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'span'],
                ALLOWED_ATTR: ['style'],
                ALLOWED_STYLES: {
                    '*': {
                        color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                    }
                },
                KEEP_CONTENT: true
            });

            const wodPayload = {
                affiliateId: selectedAffiliateId,
                classType: selectedClassType,
                wodName: wod.wodName.toUpperCase(),
                wodType: wod.type,
                description: sanitizedDescription,
                descriptionIntermediate: DOMPurify.sanitize(cleanQuillHTML(wod.descriptionIntermediate), {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'span'],
                    ALLOWED_ATTR: ['style'],
                    ALLOWED_STYLES: {
                        '*': {
                            color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                        }
                    },
                    KEEP_CONTENT: true
                }),
                descriptionBeginner: DOMPurify.sanitize(cleanQuillHTML(wod.descriptionBeginner), {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'span'],
                    ALLOWED_ATTR: ['style'],
                    ALLOWED_STYLES: {
                        '*': {
                            color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                        }
                    },
                    KEEP_CONTENT: true
                }),
                descriptionSkillWork: DOMPurify.sanitize(cleanQuillHTML(wod.descriptionSkillWork), {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'span'],
                    ALLOWED_ATTR: ['style'],
                    ALLOWED_STYLES: {
                        '*': {
                            color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                        }
                    },
                    KEEP_CONTENT: true
                }),
                descriptionStretching: DOMPurify.sanitize(cleanQuillHTML(wod.descriptionStretching), {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'span'],
                    ALLOWED_ATTR: ['style'],
                    ALLOWED_STYLES: {
                        '*': {
                            color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                        }
                    },
                    KEEP_CONTENT: true
                }),
                descriptionAtHome: DOMPurify.sanitize(cleanQuillHTML(wod.descriptionAtHome), {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'span'],
                    ALLOWED_ATTR: ['style'],
                    ALLOWED_STYLES: {
                        '*': {
                            color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
                        }
                    },
                    KEEP_CONTENT: true
                }),
                date: formattedDate,
                notes: wod.notes,
                competitionInfo: saveCompetitionToCompeteDay ? "" : sanitizedCompetitionInfo,
                videoLinks: (wod.videoLinks || []).map((link) => link.trim()).filter(Boolean),
            };

            const saveResponse = await saveTodayWOD(selectedAffiliateId, wodPayload);
            const todayWodId = saveResponse?.todayWod?.id;

            if (todayWodId && removeWorkoutImage) {
                await deleteTodayWODWorkoutImage(todayWodId);
            }
            if (todayWodId && selectedWorkoutImageFile) {
                await uploadTodayWODWorkoutImage(todayWodId, selectedWorkoutImageFile);
            }

            if (saveCompetitionToCompeteDay && typeof onSaveCompetitionInfo === "function") {
                await onSaveCompetitionInfo({
                    date: formattedDate,
                    content: sanitizedCompetitionInfo
                });
            }
            refreshWODs();
            handleClose();
            onSaveSuccess?.({ type: "success", message: "WOD saved." });
        } catch (error) {
            console.error("❌ Error saving WOD:", error);
            onSaveError?.({ type: "error", message: "Failed to save WOD." });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSelectWOD = (selectedWOD) => {
        // Convert \n -> <br> in description, then normalize
        const formattedDescription = toEditorHtml(selectedWOD.description || "");

        setWod({
            wodName: selectedWOD.name || "",
            type: selectedWOD.type || "For Time",
            description: formattedDescription,
            descriptionIntermediate: "",
            descriptionBeginner: "",
            descriptionSkillWork: "",
            descriptionStretching: "",
            descriptionAtHome: "",
            notes: "",
            competitionInfo: "",
            videoLinks: []
        });
        setShowSearch(false);
        setVideoLinkErrors([]);

        if (formattedDescription) {
            setErrors({...errors, description: false});
        }
    };

    const handleDescriptionInput = (value) => {
        setWod((prev) => ({ ...prev, description: value }));

        if (stripHtml(value) && errors.description) {
            setErrors((prev) => ({ ...prev, description: false }));
        }
    };

    const handleCompetitionInfoInput = (value) => {
        setWod((prev) => ({ ...prev, competitionInfo: value }));
    };

    const handleIntermediateInput = (value) => {
        setWod((prev) => ({ ...prev, descriptionIntermediate: value }));
        if (stripHtml(value) && errors.description) {
            setErrors((prev) => ({ ...prev, description: false }));
        }
    };

    const handleBeginnerInput = (value) => {
        setWod((prev) => ({ ...prev, descriptionBeginner: value }));
        if (stripHtml(value) && errors.description) {
            setErrors((prev) => ({ ...prev, description: false }));
        }
    };

    const handleSkillWorkInput = (value) => {
        setWod((prev) => ({ ...prev, descriptionSkillWork: value }));
        if (stripHtml(value) && errors.description) {
            setErrors((prev) => ({ ...prev, description: false }));
        }
    };

    const handleStretchingInput = (value) => {
        setWod((prev) => ({ ...prev, descriptionStretching: value }));
        if (stripHtml(value) && errors.description) {
            setErrors((prev) => ({ ...prev, description: false }));
        }
    };

    const handleAtHomeInput = (value) => {
        setWod((prev) => ({ ...prev, descriptionAtHome: value }));
        if (stripHtml(value) && errors.description) {
            setErrors((prev) => ({ ...prev, description: false }));
        }
    };

    const handleVideoLinkChange = (index, value) => {
        setWod((prev) => {
            const links = Array.isArray(prev.videoLinks) ? [...prev.videoLinks] : [];
            links[index] = value;
            return { ...prev, videoLinks: links };
        });
        setVideoLinkErrors((prev) => {
            const next = [...prev];
            next[index] = "";
            return next;
        });
    };

    const handleAddVideoLink = () => {
        setWod((prev) => ({
            ...prev,
            videoLinks: [...(Array.isArray(prev.videoLinks) ? prev.videoLinks : []), ""]
        }));
        setVideoLinkErrors((prev) => [...prev, ""]);
    };

    const handleRemoveVideoLink = (index) => {
        setWod((prev) => {
            const links = Array.isArray(prev.videoLinks) ? [...prev.videoLinks] : [];
            links.splice(index, 1);
            return { ...prev, videoLinks: links };
        });
        setVideoLinkErrors((prev) => {
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    };

    const handleWorkoutImageChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            setImageError("Only JPG, PNG, WEBP, GIF and AVIF image formats are allowed.");
            return;
        }

        if (file.size > MAX_UPLOAD_IMAGE_BYTES) {
            setImageError("Image is too large. Maximum file size is 15MB.");
            return;
        }

        setImageError("");
        try {
            const compressedFile = await compressImageInBrowser(file);
            const objectUrl = URL.createObjectURL(compressedFile);
            setSelectedWorkoutImageFile(compressedFile);
            setSelectedWorkoutImagePreview(objectUrl);
            setRemoveWorkoutImage(false);
        } catch (error) {
            setImageError(error.message || "Failed to process selected image.");
        }
    };

    const handleRemoveWorkoutImage = () => {
        if (selectedWorkoutImagePreview) {
            URL.revokeObjectURL(selectedWorkoutImagePreview);
        }
        setSelectedWorkoutImageFile(null);
        setSelectedWorkoutImagePreview("");
        setRemoveWorkoutImage(true);
        setImageError("");
    };

    const handleClose = () => {
        // Reset state when modal closes
        setWod({
            wodName: "",
            type: "For Time",
            description: "",
            descriptionIntermediate: "",
            descriptionBeginner: "",
            descriptionSkillWork: "",
            descriptionStretching: "",
            descriptionAtHome: "",
            notes: "",
            competitionInfo: "",
            videoLinks: []
        });
        setShowSearch(true);
        setErrors({ description: false });
        setVideoLinkErrors([]);
        setImageError("");
        setSelectedWorkoutImageFile(null);
        setSelectedWorkoutImagePreview("");
        setExistingWorkoutImage(null);
        setRemoveWorkoutImage(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (isSaving) return;
                handleClose();
            }}
            fullWidth
            maxWidth="md"
            slotProps={{ root: { sx: { zIndex: dialogZIndex } } }}
        >
            <DialogTitle>
                {formatDateKeyLong(toCivilDateKey(selectedDate, timeZone))}
            </DialogTitle>
            <DialogContent onPasteCapture={handleQuillPasteCapture}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Search WOD:</Typography>
                {showSearch && (
                    <WODSearch onSelectWOD={handleSelectWOD} showType={true} />
                )}
                <Divider sx={{ my: 2 }} />

                <TextField
                    fullWidth
                    label="WOD Name"
                    value={wod.wodName}
                    onChange={(e) => setWod({ ...wod, wodName: e.target.value })}
                    sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={wod.type}
                        label="Type"
                        onChange={(e) => setWod({ ...wod, type: e.target.value })}
                        MenuProps={{
                            sx: { zIndex: dialogZIndex + 1 }
                        }}
                    >
                        {wodTypes.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={2}
                    value={wod.notes}
                    onChange={(e) => setWod({ ...wod, notes: e.target.value })}
                    sx={{ mb: 2 }}
                />

                <Typography variant="subtitle1" sx={{ mb: 1 }}>Description *</Typography>
                <FormControl fullWidth error={errors.description} sx={{ mb: 1 }}>
                    <Box sx={getEditorSx(errors.description)}>
                        <ReactQuill
                            theme="snow"
                            value={wod.description}
                            onChange={handleDescriptionInput}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Description *"
                        />
                    </Box>
                    {errors.description && (
                        <FormHelperText error>At least one CAP/WOD section is required</FormHelperText>
                    )}
                </FormControl>

                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Intermediate</Typography>
                <Box sx={getEditorSx(false)}>
                    <ReactQuill
                        theme="snow"
                        value={wod.descriptionIntermediate}
                        onChange={handleIntermediateInput}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Intermediate"
                    />
                </Box>

                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Beginner</Typography>
                <Box sx={getEditorSx(false)}>
                    <ReactQuill
                        theme="snow"
                        value={wod.descriptionBeginner}
                        onChange={handleBeginnerInput}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Beginner"
                    />
                </Box>

                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Skill Work</Typography>
                <Box sx={getEditorSx(false)}>
                    <ReactQuill
                        theme="snow"
                        value={wod.descriptionSkillWork}
                        onChange={handleSkillWorkInput}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Skill Work"
                    />
                </Box>

                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Stretching</Typography>
                <Box sx={getEditorSx(false)}>
                    <ReactQuill
                        theme="snow"
                        value={wod.descriptionStretching}
                        onChange={handleStretchingInput}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Stretching"
                    />
                </Box>

                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>At Home</Typography>
                <Box sx={getEditorSx(false)}>
                    <ReactQuill
                        theme="snow"
                        value={wod.descriptionAtHome}
                        onChange={handleAtHomeInput}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="At Home"
                    />
                </Box>

                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        * At least one section is required.
                    </Typography>
                </Box>

                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Competition Info:</Typography>
                <Box
                    sx={getEditorSx(false)}
                >
                    <ReactQuill
                        theme="snow"
                        value={wod.competitionInfo}
                        onChange={handleCompetitionInfoInput}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Competition Info"
                    />
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Workout image</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Supported formats: JPG, PNG, WEBP, GIF, AVIF. Max file size: 15MB.
                    </Typography>

                    {(selectedWorkoutImagePreview || (existingWorkoutImage && !removeWorkoutImage)) && (
                        <Box sx={{ mb: 1.5 }}>
                            <Box
                                component="img"
                                src={selectedWorkoutImagePreview || existingWorkoutImage}
                                alt="WOD workout preview"
                                sx={{
                                    width: "100%",
                                    maxHeight: 260,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    border: "1px solid #ddd"
                                }}
                            />
                        </Box>
                    )}

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button variant="outlined" onClick={() => workoutImageInputRef.current?.click()}>
                            Select image
                        </Button>
                        {(selectedWorkoutImagePreview || existingWorkoutImage) && (
                            <Button variant="outlined" color="error" onClick={handleRemoveWorkoutImage}>
                                Remove image
                            </Button>
                        )}
                    </Box>
                    <input
                        ref={workoutImageInputRef}
                        style={{ display: "none" }}
                        accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                        type="file"
                        onChange={handleWorkoutImageChange}
                    />
                    {imageError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {imageError}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Videos (YouTube)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Add one or more YouTube links.
                    </Typography>

                    {(wod.videoLinks || []).map((link, index) => (
                        <Box key={`wod-video-link-${index}`} sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                            <TextField
                                fullWidth
                                label={`YouTube link ${index + 1}`}
                                value={link}
                                onChange={(e) => handleVideoLinkChange(index, e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=..."
                                error={!!videoLinkErrors[index]}
                                helperText={videoLinkErrors[index] || ""}
                            />
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleRemoveVideoLink(index)}
                            >
                                Remove
                            </Button>
                        </Box>
                    ))}

                    <Button variant="outlined" onClick={handleAddVideoLink}>
                        Add Video
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="outlined" color="inherit" disabled={isSaving}>Close</Button>
                <Button
                    onClick={handleSaveWOD}
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
                >
                    {isSaving ? "Saving..." : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}